import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Chip, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
} from '@mui/material';
import { CardMembership as SubscriptionIcon } from '@mui/icons-material';
import { apiService } from '../../services/apiService';

// Human labels + ordering for subscription entitlements shown in the detail window.
const ENTITLEMENT_LABELS = [
  ['max_users', 'Users'],
  ['max_observatories', 'Observatories'],
  ['max_projects', 'Projects'],
  ['max_sub_organizations', 'Sub-organizations'],
  ['max_organizations', 'Organizations owned'],
  ['max_storage_gb', 'Storage (GB)'],
  ['monthly_base_hours', 'Monthly base hours'],
  ['max_virtual_hours', 'Virtual hours / month (non-member sites)'],
  ['max_virtual_class', 'Max virtual class'],
  ['allowed_observation_types', 'Observation types'],
  ['public_time_control', 'Public time control'],
  ['revenue_share_eligible', 'Revenue share eligible'],
  ['api_access', 'API access'],
  ['notifications', 'Notifications'],
  ['features', 'Features'],
];

// Entitlements shown with two decimals (storage GB, monthly/virtual hours).
const DECIMAL_KEYS = new Set(['max_storage_gb', 'monthly_base_hours', 'max_virtual_hours']);
const fmtDecimalActual = (key, v) => (v == null ? '—' : (DECIMAL_KEYS.has(key) && Number.isFinite(Number(v)) ? Number(v).toFixed(2) : v));

// Format an entitlement value: booleans → Yes/No, arrays → comma list, unlimited flag honored.
const formatEntitlement = (key, value, entitlements) => {
  if (entitlements?.unlimited && (key.startsWith('max_') || key === 'monthly_base_hours')) return 'Unlimited';
  if (value == null) return key.startsWith('max_') ? 'Unlimited' : '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  if (DECIMAL_KEYS.has(key) && Number.isFinite(Number(value))) return Number(value).toFixed(2);
  return String(value);
};

/**
 * Compact card showing an observatory's resolved subscription tier (its own, or inherited from
 * the owner org). Clicking it opens a floating window with the tier description and entitlements.
 * Renders nothing until the resolved tier loads. `sx` is forwarded to the Card for sizing/placement.
 */
const ObservatorySubscriptionCard = ({ observatoryId, sx }) => {
  const [subscription, setSubscription] = useState(null);
  const [open, setOpen] = useState(false);

  const loadView = useCallback(async () => {
    if (!observatoryId) { setSubscription(null); return; }
    try {
      const res = await apiService.getObservatorySubscriptionView(observatoryId);
      setSubscription(res?.data || null);
    } catch (_) { /* leave prior state */ }
  }, [observatoryId]);

  useEffect(() => { loadView(); }, [loadView]);

  // Refetch when opening the detail window so the actuals reflect current usage (a project/member
  // created since mount would otherwise show a stale count until a full page refresh).
  const handleOpen = () => { loadView(); setOpen(true); };

  // Only surface subscription cards when limits are actually being enforced (SUBSCRIPTION_ENFORCEMENT
  // = enforce); the view endpoint reports this. off/warn → hide entirely.
  if (!subscription || !subscription.enforced) return null;

  return (
    <>
      <Card
        onClick={handleOpen}
        sx={{
          cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.03)',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
          ...sx,
        }}
      >
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <SubscriptionIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>Subscription</Typography>
            <Typography variant="caption" color="primary" sx={{ fontSize: '0.6rem' }}>Details ›</Typography>
          </Box>
          {subscription.display_name ? (
            <>
              <Chip label={subscription.display_name} size="small" color="primary" variant="outlined" />
              {subscription.inherited && subscription.source_name && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  Inherited from {subscription.source_name}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">No subscription</Typography>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SubscriptionIcon color="primary" />
          {subscription.display_name || 'Subscription'}
          {subscription.status && (
            <Chip label={subscription.status} size="small" variant="outlined" sx={{ ml: 0.5, textTransform: 'capitalize' }} />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {subscription.inherited && subscription.source_name && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Inherited from the organization <strong>{subscription.source_name}</strong>.
            </Typography>
          )}
          {subscription.description && (
            <Typography variant="body2" sx={{ mb: 2 }}>{subscription.description}</Typography>
          )}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Entitlements</Typography>
          {subscription.entitlements ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto auto', columnGap: 3, pb: 0.5, borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Entitlement</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textAlign: 'right', minWidth: 64 }}>Limit</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textAlign: 'right', minWidth: 64 }}>Actual</Typography>
              </Box>
              {ENTITLEMENT_LABELS
                .filter(([key]) => subscription.entitlements[key] !== undefined || subscription.entitlements.unlimited)
                .map(([key, label]) => {
                  const actual = subscription.actuals?.[key];
                  return (
                    <Box key={key} sx={{ display: 'grid', gridTemplateColumns: '1fr auto auto', columnGap: 3, alignItems: 'baseline' }}>
                      <Typography variant="body2" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', minWidth: 64 }}>
                        {formatEntitlement(key, subscription.entitlements[key], subscription.entitlements)}
                      </Typography>
                      <Typography variant="body2" sx={{ textAlign: 'right', minWidth: 64 }}>
                        {fmtDecimalActual(key, actual)}
                      </Typography>
                    </Box>
                  );
                })}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">No entitlement details available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ObservatorySubscriptionCard;
